import { useLogin } from '@refinedev/core';
import { Form, Input, Alert } from 'antd';
import { LogIn, Moon, Sun } from 'lucide-react';
import { useState, useEffect, type FC } from 'react';

interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginPage: FC = () => {
  const { mutate: login, isLoading } = useLogin<LoginFormValues>();
  const [error, setError] = useState<string | null>(null);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('fc-admin-theme', dark ? 'dark' : 'light');
  }, [dark]);

  const onFinish = (values: LoginFormValues) => {
    setError(null);
    login(values, {
      onError: (err) => {
        setError((err as { message?: string }).message ?? 'Email ou mot de passe incorrect.');
      },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900 transition-colors">
      <div className="w-full max-w-md px-4">
        <div className="fc-card p-8">
          {/* Logo */}
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-xl font-bold text-white shadow-lg">
              FC
            </div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Feedback Orchestrator</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Panneau d&apos;administration
            </p>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className="mb-4"
              onClose={() => setError(null)}
            />
          )}

          <Form<LoginFormValues>
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email
                </span>
              }
              rules={[
                { required: true, message: 'Veuillez saisir votre email' },
                { type: 'email', message: 'Email invalide' },
              ]}
            >
              <Input size="large" placeholder="admin@feedback-copilot.com" autoFocus />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Mot de passe
                </span>
              }
              rules={[{ required: true, message: 'Veuillez saisir votre mot de passe' }]}
            >
              <Input.Password size="large" placeholder="••••••••" />
            </Form.Item>

            <Form.Item className="mb-0">
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
              >
                <LogIn className="h-4 w-4" />
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </button>
            </Form.Item>
          </Form>
        </div>

        {/* Dark mode toggle */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setDark((d) => !d)}
            className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
          >
            {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            {dark ? 'Mode clair' : 'Mode sombre'}
          </button>
        </div>
      </div>
    </div>
  );
};
